// Visée au gyroscope : écoute l'orientation du téléphone et émet, à chaque mesure, la direction
// visée par son dos (voir core/orientation.js). Le jeu en tire un déplacement relatif de la caméra :
// rien à calibrer, et le glisser au doigt reste utilisable en même temps.
//
// Contraintes navigateur : les capteurs ne sont exposés qu'en contexte sécurisé (HTTPS ou
// localhost) ; sur iPhone/iPad, il faut en plus une autorisation demandée lors d'un geste de
// l'utilisateur (DeviceOrientationEvent.requestPermission). Un ordinateur sans capteur envoie au
// mieux une mesure vide : on ne considère le gyroscope actif qu'après une vraie mesure.
import { viewDirection } from '../core/orientation.js';

/** Le navigateur peut-il, en principe, fournir l'orientation ? (contexte sécurisé + API présente) */
export function isGyroSupported(win = window) {
  return !!win.isSecureContext && typeof win.DeviceOrientationEvent !== 'undefined';
}

/** Une autorisation explicite (geste utilisateur) est-elle requise ? (Safari iOS 13+) */
export function needsGyroPermission(win = window) {
  return typeof win.DeviceOrientationEvent?.requestPermission === 'function';
}

/**
 * @param {object} handlers
 * @param {(dir: {yaw:number,pitch:number}) => void} handlers.onDirection  à chaque mesure valide
 * @param {(live: boolean) => void} [handlers.onLiveChange]  true à la 1re mesure réelle, false à l'arrêt
 * @param {Window} [win]
 */
export function createGyro({ onDirection, onLiveChange }, win = window) {
  let listening = false;
  let live = false;

  const setLive = (next) => {
    if (live === next) return;
    live = next;
    onLiveChange?.(live);
  };

  const onOrientation = (e) => {
    const dir = viewDirection(e.alpha, e.beta, e.gamma);
    if (!dir) return; // ordinateur sans capteur : mesure vide
    setLive(true);
    onDirection(dir);
  };

  /**
   * Démarre l'écoute. À appeler depuis un geste utilisateur (tap) si needsGyroPermission().
   * @returns {Promise<'on'|'denied'|'unsupported'>}
   */
  async function start() {
    if (!isGyroSupported(win)) return 'unsupported';
    if (needsGyroPermission(win)) {
      try {
        if (await win.DeviceOrientationEvent.requestPermission() !== 'granted') return 'denied';
      } catch {
        return 'denied'; // appel hors geste utilisateur, ou refus
      }
    }
    if (!listening) {
      win.addEventListener('deviceorientation', onOrientation);
      listening = true;
    }
    return 'on';
  }

  function stop() {
    if (listening) win.removeEventListener('deviceorientation', onOrientation);
    listening = false;
    setLive(false);
  }

  return { start, stop, isLive: () => live, isListening: () => listening };
}
